import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { buildWeek, buildSession, swap, nextPrescription } from '../services/planService';
import { getLastSets, upsertLastSet, upsertExerciseLog, createInProgressWorkout, updateInProgressWorkout, markWorkoutCompleted, getInProgressWorkouts } from '../services/logService';
import { getSessionOverride, applySessionOverride } from '../services/overrideService';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { ChevronLeft, ChevronRight, Check, Plus, Minus, RefreshCw, X, Info } from 'lucide-react';
import exercisesData from '../data/exercises.json';
import toast from 'react-hot-toast';
import ExerciseInfoModal from '../components/ExerciseInfoModal';

function Session() {
  const { day } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [session, setSession] = useState(null);
  const [prefs, setPrefs] = useState(null);
  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);
  const [workoutState, setWorkoutState] = useState({});
  const [prescriptions, setPrescriptions] = useState({});
  const [activeSetIndex, setActiveSetIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [restTimer, setRestTimer] = useState(null);
  const [restEndTime, setRestEndTime] = useState(null);
  const [showExerciseInfo, setShowExerciseInfo] = useState(false);
  const [inProgressWorkoutId, setInProgressWorkoutId] = useState(null);

  useEffect(() => {
    const loadSession = async () => {
      if (!user) return;
      try {
        const userRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userRef);
        if (userDoc.exists()) {
          const userPrefs = userDoc.data().prefs || {};
          setPrefs(userPrefs);

          // Check if resuming an existing workout
          const resumeId = searchParams.get('resume');
          if (resumeId) {
            const workoutRef = doc(db, 'users', user.uid, 'workouts', resumeId);
            const workoutDoc = await getDoc(workoutRef);

            if (workoutDoc.exists()) {
              const workoutData = workoutDoc.data();
              // Reconstruct session from saved workout
              const sessionPlan = {
                tier: workoutData.tier,
                minutes: workoutData.minutes,
                focus: workoutData.focus,
                exercises: workoutData.exercises.map(ex => ({
                  exerciseId: ex.exerciseId,
                  name: ex.name,
                  sets: ex.sets.length,
                  reps: ex.sets[0]?.reps || null,
                  restSeconds: 90, // default
                  movementPattern: ex.movementPattern,
                  muscleGroup: ex.muscleGroup,
                })),
              };

              setSession(sessionPlan);
              setInProgressWorkoutId(resumeId);

              // Restore workout state
              const restoredState = {};
              const restoredPrescriptions = {};
              workoutData.exercises.forEach((ex, idx) => {
                restoredState[idx] = ex.sets;
                const exerciseDetails = exercisesData.find(e => e.id === ex.exerciseId);
                restoredPrescriptions[idx] = { unit: userPrefs.units || 'lb' };
              });

              setWorkoutState(restoredState);
              setPrescriptions(restoredPrescriptions);
              setLoading(false);
              return;
            }
          }

          let sessionPlan;

          if (day === 'quick') {
            const tier = parseInt(searchParams.get('tier')) || 2;
            const minutes = parseInt(searchParams.get('minutes')) || 30;
            const focus = searchParams.get('focus') || 'full-body';

            sessionPlan = buildSession(exercisesData, {
              tier,
              minutes,
              focus,
              seed: `quick-${Date.now()}`,
              excludedIds: userPrefs.excludedExerciseIds || [],
            });
          } else {
            const plan = buildWeek(exercisesData, {
              tier: userPrefs.defaultTier ?? 1,
              minutes: userPrefs.defaultDurationMin ?? 45,
              daysPerWeek: userPrefs.daysPerWeek ?? 3,
              seed: 'wk',
              excludedIds: userPrefs.excludedExerciseIds || [],
            });

            const dayIndex = parseInt(day);
            if (dayIndex >= 0 && dayIndex < plan.length) {
              sessionPlan = plan[dayIndex];
            }
          }

          if (sessionPlan) {
            // Apply overrides for today's date
            const today = new Date().toISOString().split('T')[0];
            const override = await getSessionOverride(user.uid, today);

            if (override && override.focus === sessionPlan.focus) {
              sessionPlan = applySessionOverride(sessionPlan, override, exercisesData);
            }

            setSession(sessionPlan);

            const exerciseIds = sessionPlan.exercises.map(ex => ex.exerciseId);
            const lastSets = await getLastSets(user.uid, exerciseIds);

            const initialState = {};
            const initialPrescriptions = {};
            sessionPlan.exercises.forEach((ex, idx) => {
              const exerciseDetails = exercisesData.find(e => e.id === ex.exerciseId);
              const lastSet = lastSets[ex.exerciseId];
              const setsCount = ex.sets;

              const prescription = nextPrescription(exerciseDetails, lastSet, userPrefs.units || 'lb');
              initialPrescriptions[idx] = prescription;

              initialState[idx] = Array(setsCount).fill().map(() => ({
                weight: prescription.weight ?? lastSet?.weight ?? null,
                unit: prescription.unit ?? userPrefs.units ?? 'lb',
                reps: prescription.targetReps ?? lastSet?.reps ?? null,
                durationSeconds: prescription.targetSeconds ?? lastSet?.durationSeconds ?? null,
                done: false,
              }));
            });
            setWorkoutState(initialState);
            setPrescriptions(initialPrescriptions);

            // Create in-progress workout for recovery
            try {
              const workoutId = await createInProgressWorkout(user.uid, {
                tier: sessionPlan.tier || userPrefs.defaultTier,
                minutes: sessionPlan.minutes || userPrefs.defaultDurationMin,
                focus: sessionPlan.focus,
                exercises: sessionPlan.exercises.map((ex, idx) => ({
                  exerciseId: ex.exerciseId,
                  name: ex.name,
                  sets: initialState[idx] || [],
                })),
              });
              setInProgressWorkoutId(workoutId);

              // Also save to localStorage as backup
              localStorage.setItem('gymbo_active_workout', JSON.stringify({
                workoutId,
                day,
                focus: sessionPlan.focus,
                startedAt: Date.now(),
              }));
            } catch (error) {
              console.error('Error creating in-progress workout:', error);
              // Continue anyway - not critical
            }
          }
        }
      } catch (error) {
        console.error('Error loading session:', error);
      } finally {
        setLoading(false);
      }
    };
    loadSession();
  }, [user, day, searchParams]);

  // Save workout state incrementally to Firestore and localStorage
  useEffect(() => {
    if (!user || !inProgressWorkoutId || !session || Object.keys(workoutState).length === 0) return;

    const saveState = async () => {
      try {
        const exercises = session.exercises.map((ex, idx) => ({
          exerciseId: ex.exerciseId,
          name: ex.name,
          sets: workoutState[idx] || [],
        }));

        // Save to Firestore
        await updateInProgressWorkout(user.uid, inProgressWorkoutId, exercises);

        // Save to localStorage as backup
        localStorage.setItem(`gymbo_workout_state_${inProgressWorkoutId}`, JSON.stringify({
          workoutState,
          currentExerciseIndex,
          lastSaved: Date.now(),
        }));
      } catch (error) {
        console.error('Error saving workout state:', error);
        // Don't throw - we'll retry on next change
      }
    };

    // Debounce saves by 1 second
    const timeoutId = setTimeout(saveState, 1000);
    return () => clearTimeout(timeoutId);
  }, [workoutState, user, inProgressWorkoutId, session, currentExerciseIndex]);

  // Rest timer countdown
  useEffect(() => {
    if (!restEndTime) return;

    const interval = setInterval(() => {
      const now = Date.now();
      const remaining = Math.max(0, Math.ceil((restEndTime - now) / 1000));
      setRestTimer(remaining);

      if (remaining === 0) {
        clearInterval(interval);
      }
    }, 100);

    return () => clearInterval(interval);
  }, [restEndTime]);

  const handleSetChange = (setIndex, field, value) => {
    const newSets = [...(workoutState[currentExerciseIndex] || [])];
    newSets[setIndex] = { ...newSets[setIndex], [field]: value };
    setWorkoutState({ ...workoutState, [currentExerciseIndex]: newSets });
  };

  const handleToggleDone = (setIndex) => {
    const sets = workoutState[currentExerciseIndex] || [];
    const newSets = [...sets];
    const wasDone = newSets[setIndex].done;

    newSets[setIndex] = { ...newSets[setIndex], done: !wasDone };
    setWorkoutState({ ...workoutState, [currentExerciseIndex]: newSets });

    // Start rest timer when marking a set as done
    if (!wasDone && session) {
      const exercise = session.exercises[currentExerciseIndex];
      const restSeconds = exercise.restSeconds || 90;
      setRestEndTime(Date.now() + restSeconds * 1000);
      setRestTimer(restSeconds);
    }

    // Move to next set if this one was just completed
    if (!wasDone && setIndex === activeSetIndex) {
      const nextIndex = setIndex + 1;
      if (nextIndex < sets.length) {
        setActiveSetIndex(nextIndex);
      }
    }
  };

  const handleStepValue = (setIndex, field, delta) => {
    const sets = workoutState[currentExerciseIndex] || [];
    const currentValue = parseFloat(sets[setIndex][field]) || 0;
    const newValue = Math.max(0, currentValue + delta);
    handleSetChange(setIndex, field, newValue);
  };

  const handleDeleteSet = (setIndex) => {
    const sets = workoutState[currentExerciseIndex] || [];
    const newSets = sets.filter((_, idx) => idx !== setIndex);
    setWorkoutState({ ...workoutState, [currentExerciseIndex]: newSets });

    // Adjust active set index if needed
    if (activeSetIndex >= newSets.length) {
      setActiveSetIndex(Math.max(0, newSets.length - 1));
    } else if (activeSetIndex > setIndex) {
      setActiveSetIndex(activeSetIndex - 1);
    }

    toast.success('Set deleted');
  };

  const handleAddSet = () => {
    const sets = workoutState[currentExerciseIndex] || [];
    const lastSet = sets[sets.length - 1];

    const newSet = {
      weight: lastSet?.weight ?? null,
      unit: lastSet?.unit ?? (prefs?.units || 'lb'),
      reps: null,
      durationSeconds: null,
      done: false,
      unprescribed: true,
    };

    const newSets = [...sets, newSet];
    setWorkoutState({ ...workoutState, [currentExerciseIndex]: newSets });
    toast.success('Set added');
  };

  const handleNextExercise = () => {
    if (currentExerciseIndex < session.exercises.length - 1) {
      setCurrentExerciseIndex(currentExerciseIndex + 1);
      setActiveSetIndex(0);
    }
  };

  const handlePrevExercise = () => {
    if (currentExerciseIndex > 0) {
      setCurrentExerciseIndex(currentExerciseIndex - 1);
      setActiveSetIndex(0);
    }
  };

  const handleSwap = async () => {
    if (!session || !prefs || !user) return;

    const currentExercise = session.exercises[currentExerciseIndex];
    const swapped = swap(exercisesData, currentExercise.exerciseId, prefs.defaultTier);

    if (swapped) {
      const newExercises = [...session.exercises];
      newExercises[currentExerciseIndex] = {
        exerciseId: swapped.id,
        name: swapped.name,
        sets: swapped.defaultSets,
        reps: swapped.defaultReps,
        restSeconds: swapped.defaultRestSeconds,
        movementPattern: swapped.movementPattern,
        muscleGroup: swapped.muscleGroup,
        minTier: swapped.minTier,
        substitutes: swapped.substitutes,
      };
      setSession({ ...session, exercises: newExercises });

      const lastSets = await getLastSets(user.uid, [swapped.id]);
      const lastSet = lastSets[swapped.id];
      const exerciseDetails = exercisesData.find(e => e.id === swapped.id);
      const prescription = nextPrescription(exerciseDetails, lastSet, prefs.units || 'lb');
      setPrescriptions({ ...prescriptions, [currentExerciseIndex]: prescription });

      const setsCount = swapped.defaultSets;
      const newSets = Array(setsCount).fill().map(() => ({
        weight: prescription.weight ?? lastSet?.weight ?? null,
        unit: prescription.unit ?? prefs.units ?? 'lb',
        reps: prescription.targetReps ?? lastSet?.reps ?? null,
        durationSeconds: prescription.targetSeconds ?? lastSet?.durationSeconds ?? null,
        done: false,
      }));
      setWorkoutState({ ...workoutState, [currentExerciseIndex]: newSets });
      setActiveSetIndex(0);
      toast.success('Exercise swapped');
    } else {
      toast.error('No substitute available');
    }
  };

  const handleFinish = async () => {
    if (!user || !session || !inProgressWorkoutId) return;

    try {
      const exercises = session.exercises.map((ex, idx) => ({
        exerciseId: ex.exerciseId,
        name: ex.name,
        sets: workoutState[idx] || [],
        notes: '',
      }));

      // Mark the in-progress workout as completed
      await markWorkoutCompleted(user.uid, inProgressWorkoutId, {
        exercises,
      });

      const exerciseIds = session.exercises.map(ex => ex.exerciseId);
      const currentLastSets = await getLastSets(user.uid, exerciseIds);

      const lastSetUpdates = session.exercises.map(async (ex, idx) => {
        const sets = workoutState[idx] || [];
        const completedSets = sets.filter(s => s.done);
        // For progression logic, only count prescribed sets
        const prescribedCompletedSets = completedSets.filter(s => !s.unprescribed);

        if (completedSets.length > 0) {
          const firstCompleted = completedSets[0];
          const exerciseDetails = exercisesData.find(e => e.id === ex.exerciseId);
          const currentLastSet = currentLastSets[ex.exerciseId];

          let missStreak = 0;
          if (exerciseDetails.progression === 'load' || exerciseDetails.progression === 'reps_first') {
            const range = exerciseDetails.defaultReps;
            const [rangeBottom] = range.split('-').map(Number);
            const firstSetReps = firstCompleted.reps ? parseInt(firstCompleted.reps) : 0;

            if (firstSetReps < rangeBottom) {
              missStreak = (currentLastSet?.missStreak ?? 0) + 1;
            } else {
              missStreak = 0;
            }
          }

          await upsertLastSet(user.uid, ex.exerciseId, {
            weight: firstCompleted.weight ?? null,
            unit: firstCompleted.unit ?? prefs.units ?? 'lb',
            reps: firstCompleted.reps ?? null,
            durationSeconds: firstCompleted.durationSeconds ?? null,
            sets: prescribedCompletedSets.length, // Only count prescribed sets for progression
            missStreak,
          });

          const topSet = completedSets.reduce((max, set) => {
            const weight = parseFloat(set.weight) || 0;
            const maxWeight = parseFloat(max.weight) || 0;
            return weight > maxWeight ? set : max;
          }, completedSets[0]);

          await upsertExerciseLog(user.uid, ex.exerciseId, {
            date: new Date().toISOString(),
            topWeight: topSet.weight ? parseFloat(topSet.weight) : null,
            topReps: topSet.reps ? parseInt(topSet.reps) : null,
            durationSeconds: topSet.durationSeconds ? parseInt(topSet.durationSeconds) : null,
            unit: topSet.unit ?? prefs.units ?? 'lb',
          });
        }
      });

      await Promise.all(lastSetUpdates);

      // Clean up localStorage
      localStorage.removeItem('gymbo_active_workout');
      localStorage.removeItem(`gymbo_workout_state_${inProgressWorkoutId}`);

      toast.success('Workout saved');
      navigate('/history');
    } catch (error) {
      console.error('Error saving workout:', error);
      toast.error('Error saving workout');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-light-bg dark:bg-dark-bg">
        <div className="text-lg text-light-ink dark:text-dark-ink font-plex">Loading...</div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-light-bg dark:bg-dark-bg">
        <div className="text-light-muted dark:text-dark-muted font-plex">Session not found</div>
      </div>
    );
  }

  const currentExercise = session.exercises[currentExerciseIndex];
  const exerciseDetails = exercisesData.find(e => e.id === currentExercise.exerciseId);
  const sets = workoutState[currentExerciseIndex] || [];
  const prescription = prescriptions[currentExerciseIndex];
  const completedExerciseSets = session.exercises.reduce((sum, _, idx) => {
    const exSets = workoutState[idx] || [];
    return sum + exSets.filter(s => s.done).length;
  }, 0);
  const totalSets = session.exercises.reduce((sum, _, idx) => {
    const exSets = workoutState[idx] || [];
    return sum + exSets.length;
  }, 0);
  const progress = totalSets > 0 ? (completedExerciseSets / totalSets) * 100 : 0;

  const tracking = exerciseDetails?.tracking || 'weight_reps';

  const formatRestTimer = (seconds) => {
    if (seconds === null) return '--:--';
    if (seconds === 0) return 'GO';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen bg-light-bg dark:bg-dark-bg">
      {/* Session Header */}
      <div className="bg-light-surface dark:bg-dark-surface border-b border-light-border dark:border-dark-border">
        <div className="h-[5px] bg-light-border dark:bg-dark-border">
          <div
            className="h-full bg-light-accent dark:bg-dark-accent transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="px-4 py-3">
          <div className="text-[13px] font-plex font-medium text-light-muted dark:text-dark-muted uppercase tracking-[0.09em]">
            {session.focus}
          </div>
          <div className="mt-1 text-[13px] font-plex text-light-muted dark:text-dark-muted">
            Exercise {currentExerciseIndex + 1} of {session.exercises.length} · {completedExerciseSets} of {totalSets} sets
          </div>
        </div>
      </div>

      {/* Exercise Content */}
      <div className="px-4 py-4 pb-safe">
        {/* Exercise Name with Info Button */}
        <div className="flex items-start gap-2 mb-4">
          <h1 className="text-[30px] font-grotesk font-bold text-light-ink dark:text-dark-ink tracking-[-0.02em] flex-1 leading-tight break-words">
            {currentExercise.name}
          </h1>
          <button
            onClick={() => setShowExerciseInfo(true)}
            className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-[12px] bg-light-surface-2 dark:bg-dark-surface-2 hover:bg-light-border dark:hover:bg-dark-border transition-colors"
          >
            <Info size={20} className="text-light-ink dark:text-dark-ink" />
          </button>
        </div>

        {/* Progression Reason */}
        {prescription && prescription.reason && (
          <div className="mb-4 p-4 bg-light-accent-soft dark:bg-dark-accent-soft rounded-[16px]">
            <p className="text-[15px] font-plex font-medium text-light-ink dark:text-dark-ink leading-relaxed">
              {prescription.reason}
            </p>
          </div>
        )}

        {/* Exercise Images */}
        {exerciseDetails?.images && exerciseDetails.images.length > 0 && (
          <div className="mb-4 flex gap-2 overflow-x-auto">
            {exerciseDetails.images.map((img, i) => (
              <img
                key={i}
                src={img}
                alt={`${currentExercise.name} ${i + 1}`}
                className="h-32 rounded-[14px] object-cover"
              />
            ))}
          </div>
        )}

        {/* Set Rows */}
        <div className="space-y-2 mb-6">
          {sets.map((set, setIndex) => {
            const isActive = setIndex === activeSetIndex;
            const isDone = set.done;
            const isPending = !isDone && setIndex > activeSetIndex;

            return (
              <div
                key={setIndex}
                className={`rounded-[16px] px-3 sm:px-4 transition-all ${
                  isDone
                    ? 'bg-light-accent dark:bg-dark-accent'
                    : isActive
                    ? 'border-2 border-light-ink dark:border-dark-accent bg-light-surface dark:bg-dark-surface'
                    : 'bg-light-surface dark:bg-dark-surface border border-light-border dark:border-dark-border'
                } ${isActive && !isDone ? 'py-3' : 'h-[76px]'}`}
              >
                {/* Single-line layout for non-active rows */}
                {(!isActive || isDone) && (
                  <div className="h-full flex items-center">
                    {/* Set Number */}
                    <div className={`text-[26px] font-grotesk font-bold tabular-nums w-10 sm:w-12 ${
                      isDone
                        ? 'text-white dark:text-[#06121f]'
                        : isPending
                        ? 'text-light-disabled dark:text-dark-faint'
                        : 'text-light-ink dark:text-dark-ink'
                    }`}>
                      {setIndex + 1}
                    </div>

                    {/* Input Fields */}
                    <div className="flex-1 flex items-center gap-1.5 sm:gap-2 mx-2 sm:mx-4">
                      {tracking === 'weight_reps' && (
                        <>
                          <div className="flex items-center gap-1">
                            <input
                              type="number"
                              value={set.weight ?? ''}
                              onChange={(e) => handleSetChange(setIndex, 'weight', e.target.value)}
                              className={`w-14 sm:w-16 h-10 text-center text-[14px] sm:text-[15px] font-grotesk font-semibold tabular-nums rounded-[12px] border ${
                                isDone
                                  ? 'bg-transparent text-white dark:text-[#06121f] border-white/30'
                                  : 'bg-light-surface dark:bg-dark-surface-2 text-light-ink dark:text-dark-ink border-light-border dark:border-dark-border'
                              }`}
                            />
                            <span className={`text-[12px] sm:text-[13px] font-plex ${
                              isDone ? 'text-white/90 dark:text-[#06121f]/90' : 'text-light-muted dark:text-dark-muted'
                            }`}>
                              {set.unit || 'lb'}
                            </span>
                          </div>
                          <span className={`text-[14px] sm:text-[15px] font-plex ${
                            isDone ? 'text-white/90 dark:text-[#06121f]/90' : 'text-light-muted dark:text-dark-muted'
                          }`}>×</span>
                          <input
                            type="number"
                            value={set.reps ?? ''}
                            onChange={(e) => handleSetChange(setIndex, 'reps', e.target.value)}
                            className={`w-12 sm:w-14 h-10 text-center text-[14px] sm:text-[15px] font-grotesk font-semibold tabular-nums rounded-[12px] border ${
                              isDone
                                ? 'bg-transparent text-white dark:text-[#06121f] border-white/30'
                                : 'bg-light-surface dark:bg-dark-surface-2 text-light-ink dark:text-dark-ink border-light-border dark:border-dark-border'
                            }`}
                          />
                        </>
                      )}

                      {tracking === 'reps' && (
                        <input
                          type="number"
                          value={set.reps ?? ''}
                          onChange={(e) => handleSetChange(setIndex, 'reps', e.target.value)}
                          placeholder="reps"
                          className={`w-16 sm:w-20 h-10 text-center text-[14px] sm:text-[15px] font-grotesk font-semibold tabular-nums rounded-[12px] border ${
                            isDone
                              ? 'bg-transparent text-white dark:text-[#06121f] border-white/30'
                              : 'bg-light-surface dark:bg-dark-surface-2 text-light-ink dark:text-dark-ink border-light-border dark:border-dark-border'
                          }`}
                        />
                      )}

                      {tracking === 'duration' && (
                        <div className="flex items-center gap-1">
                          <input
                            type="number"
                            value={set.durationSeconds ?? ''}
                            onChange={(e) => handleSetChange(setIndex, 'durationSeconds', e.target.value)}
                            className={`w-14 sm:w-16 h-10 text-center text-[14px] sm:text-[15px] font-grotesk font-semibold tabular-nums rounded-[12px] border ${
                              isDone
                                ? 'bg-transparent text-white dark:text-[#06121f] border-white/30'
                                : 'bg-light-surface dark:bg-dark-surface-2 text-light-ink dark:text-dark-ink border-light-border dark:border-dark-border'
                            }`}
                          />
                          <span className={`text-[12px] sm:text-[13px] font-plex ${
                            isDone ? 'text-white/90 dark:text-[#06121f]/90' : 'text-light-muted dark:text-dark-muted'
                          }`}>sec</span>
                        </div>
                      )}
                    </div>

                    {/* Complete/Delete Buttons */}
                    <div className="flex gap-2 shrink-0">
                      {isDone && (
                        <button
                          onClick={() => handleDeleteSet(setIndex)}
                          className="w-11 h-11 sm:w-[52px] sm:h-[52px] rounded-[14px] sm:rounded-[16px] flex items-center justify-center bg-white/20 dark:bg-black/20 hover:bg-white/30 dark:hover:bg-black/30 transition-colors"
                        >
                          <X
                            size={22}
                            className="text-white dark:text-[#06121f]"
                            strokeWidth={2.5}
                          />
                        </button>
                      )}
                      <button
                        onClick={() => handleToggleDone(setIndex)}
                        className={`w-11 h-11 sm:w-[52px] sm:h-[52px] rounded-[14px] sm:rounded-[16px] flex items-center justify-center ${
                          isDone
                            ? 'bg-white/20 dark:bg-black/20 hover:bg-white/30 dark:hover:bg-black/30'
                            : 'bg-light-accent dark:bg-dark-accent'
                        } transition-colors`}
                      >
                        <Check
                          size={22}
                          className={isDone ? 'text-white dark:text-[#06121f]' : 'text-white'}
                          strokeWidth={3}
                        />
                      </button>
                    </div>
                  </div>
                )}

                {/* Two-line layout for active rows */}
                {isActive && !isDone && (
                  <div className="space-y-3">
                    {/* Line 1: Set number + Inputs */}
                    <div className="flex items-center">
                      <div className={`text-[26px] font-grotesk font-bold tabular-nums w-10 sm:w-12 text-light-ink dark:text-dark-ink`}>
                        {setIndex + 1}
                      </div>

                      <div className="flex-1 flex items-center gap-1.5 sm:gap-2 mx-2 sm:mx-4">
                        {tracking === 'weight_reps' && (
                          <>
                            <div className="flex items-center gap-1">
                              <input
                                type="number"
                                value={set.weight ?? ''}
                                onChange={(e) => handleSetChange(setIndex, 'weight', e.target.value)}
                                className="w-14 sm:w-16 h-10 text-center text-[14px] sm:text-[15px] font-grotesk font-semibold tabular-nums rounded-[12px] border bg-light-surface dark:bg-dark-surface-2 text-light-ink dark:text-dark-ink border-light-border dark:border-dark-border"
                              />
                              <span className="text-[12px] sm:text-[13px] font-plex text-light-muted dark:text-dark-muted">
                                {set.unit || 'lb'}
                              </span>
                            </div>
                            <span className="text-[14px] sm:text-[15px] font-plex text-light-muted dark:text-dark-muted">×</span>
                            <input
                              type="number"
                              value={set.reps ?? ''}
                              onChange={(e) => handleSetChange(setIndex, 'reps', e.target.value)}
                              className="w-12 sm:w-14 h-10 text-center text-[14px] sm:text-[15px] font-grotesk font-semibold tabular-nums rounded-[12px] border bg-light-surface dark:bg-dark-surface-2 text-light-ink dark:text-dark-ink border-light-border dark:border-dark-border"
                            />
                          </>
                        )}

                        {tracking === 'reps' && (
                          <input
                            type="number"
                            value={set.reps ?? ''}
                            onChange={(e) => handleSetChange(setIndex, 'reps', e.target.value)}
                            placeholder="reps"
                            className="w-16 sm:w-20 h-10 text-center text-[14px] sm:text-[15px] font-grotesk font-semibold tabular-nums rounded-[12px] border bg-light-surface dark:bg-dark-surface-2 text-light-ink dark:text-dark-ink border-light-border dark:border-dark-border"
                          />
                        )}

                        {tracking === 'duration' && (
                          <div className="flex items-center gap-1">
                            <input
                              type="number"
                              value={set.durationSeconds ?? ''}
                              onChange={(e) => handleSetChange(setIndex, 'durationSeconds', e.target.value)}
                              className="w-14 sm:w-16 h-10 text-center text-[14px] sm:text-[15px] font-grotesk font-semibold tabular-nums rounded-[12px] border bg-light-surface dark:bg-dark-surface-2 text-light-ink dark:text-dark-ink border-light-border dark:border-dark-border"
                            />
                            <span className="text-[12px] sm:text-[13px] font-plex text-light-muted dark:text-dark-muted">sec</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Line 2: Steppers + Action button */}
                    <div className="flex items-center justify-between">
                      {/* Steppers */}
                      {tracking !== 'duration' && (
                        <div className="flex gap-1.5 sm:gap-2">
                          {tracking === 'weight_reps' && (
                            <>
                              <button
                                onClick={() => handleStepValue(setIndex, 'weight', -2.5)}
                                className="w-10 sm:w-11 h-10 flex items-center justify-center rounded-[12px] bg-light-border dark:bg-dark-border hover:bg-light-disabled dark:hover:bg-dark-faint transition-colors"
                              >
                                <Minus size={16} className="text-light-ink dark:text-dark-ink" />
                              </button>
                              <button
                                onClick={() => handleStepValue(setIndex, 'weight', 2.5)}
                                className="w-10 sm:w-11 h-10 flex items-center justify-center rounded-[12px] bg-light-border dark:bg-dark-border hover:bg-light-disabled dark:hover:bg-dark-faint transition-colors"
                              >
                                <Plus size={16} className="text-light-ink dark:text-dark-ink" />
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => handleStepValue(setIndex, 'reps', -1)}
                            className="w-10 sm:w-11 h-10 flex items-center justify-center rounded-[12px] bg-light-border dark:bg-dark-border hover:bg-light-disabled dark:hover:bg-dark-faint transition-colors"
                          >
                            <Minus size={16} className="text-light-ink dark:text-dark-ink" />
                          </button>
                          <button
                            onClick={() => handleStepValue(setIndex, 'reps', 1)}
                            className="w-10 sm:w-11 h-10 flex items-center justify-center rounded-[12px] bg-light-border dark:bg-dark-border hover:bg-light-disabled dark:hover:bg-dark-faint transition-colors"
                          >
                            <Plus size={16} className="text-light-ink dark:text-dark-ink" />
                          </button>
                        </div>
                      )}

                      {/* Complete button */}
                      <button
                        onClick={() => handleToggleDone(setIndex)}
                        className="w-12 h-10 sm:w-14 sm:h-11 rounded-[14px] flex items-center justify-center bg-light-accent dark:bg-dark-accent hover:opacity-90 transition-opacity"
                      >
                        <Check
                          size={20}
                          className="text-white"
                          strokeWidth={3}
                        />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {/* Add Set Button */}
          <button
            onClick={handleAddSet}
            className="h-[58px] rounded-[16px] border-2 border-dashed border-light-border dark:border-dark-border hover:border-light-accent dark:hover:border-dark-accent bg-light-surface dark:bg-dark-surface hover:bg-light-surface-2 dark:hover:bg-dark-surface-2 flex items-center justify-center gap-2 transition-colors"
          >
            <Plus size={20} className="text-light-muted dark:text-dark-muted" />
            <span className="text-[15px] font-plex font-medium text-light-muted dark:text-dark-muted">
              Add Set
            </span>
          </button>
        </div>

        {/* Rest Timer and Actions */}
        <div className="flex items-center gap-3 mb-6">
          {/* Rest Timer */}
          {restTimer !== null && (
            <button
              onClick={() => {
                if (restTimer === 0) {
                  setRestTimer(null);
                  setRestEndTime(null);
                } else {
                  const restSeconds = currentExercise.restSeconds || 90;
                  setRestEndTime(Date.now() + restSeconds * 1000);
                  setRestTimer(restSeconds);
                }
              }}
              className={`h-[58px] w-[104px] rounded-[17px] flex flex-col items-center justify-center ${
                restTimer === 0
                  ? 'bg-light-accent dark:bg-dark-accent'
                  : 'bg-light-surface-2 dark:bg-dark-surface-2'
              }`}
            >
              <div className={`text-[21px] font-grotesk font-semibold tabular-nums ${
                restTimer === 0
                  ? 'text-white'
                  : 'text-light-ink dark:text-dark-ink'
              }`}>
                {formatRestTimer(restTimer)}
              </div>
              <div className={`text-[10.5px] font-plex font-semibold uppercase tracking-wider ${
                restTimer === 0
                  ? 'text-white/80'
                  : 'text-light-muted dark:text-dark-muted'
              }`}>
                REST
              </div>
            </button>
          )}

          {/* Swap Button */}
          <button
            onClick={handleSwap}
            className="h-[58px] px-5 rounded-[17px] bg-light-surface dark:bg-dark-surface border border-light-border dark:border-dark-border flex items-center justify-center gap-2"
          >
            <RefreshCw size={18} className="text-light-muted dark:text-dark-muted" />
            <span className="text-[15px] font-plex font-medium text-light-ink dark:text-dark-ink">
              Swap
            </span>
          </button>
        </div>

        {/* Navigation */}
        <div className="flex gap-2 sm:gap-3">
          <button
            onClick={handlePrevExercise}
            disabled={currentExerciseIndex === 0}
            className="h-[58px] flex-1 min-w-0 rounded-[17px] bg-light-surface dark:bg-dark-surface border border-light-border dark:border-dark-border disabled:opacity-30 flex items-center justify-center gap-2"
          >
            <ChevronLeft size={20} className="text-light-ink dark:text-dark-ink flex-shrink-0" />
            <span className="text-[15px] font-plex font-semibold text-light-ink dark:text-dark-ink truncate">
              Previous
            </span>
          </button>

          {currentExerciseIndex < session.exercises.length - 1 ? (
            <button
              onClick={handleNextExercise}
              className="h-[58px] flex-1 min-w-0 rounded-[17px] bg-light-accent dark:bg-dark-accent flex items-center justify-center gap-2"
            >
              <span className="text-[15px] font-plex font-semibold text-white truncate">
                Next
              </span>
              <ChevronRight size={20} className="text-white flex-shrink-0" />
            </button>
          ) : (
            <button
              onClick={handleFinish}
              className="h-[58px] flex-1 min-w-0 rounded-[17px] bg-light-accent dark:bg-dark-accent flex items-center justify-center"
            >
              <span className="text-[15px] font-plex font-semibold text-white truncate">
                Finish Workout
              </span>
            </button>
          )}
        </div>
      </div>

      {/* Exercise Info Modal */}
      <ExerciseInfoModal
        exercise={currentExercise}
        isOpen={showExerciseInfo}
        onClose={() => setShowExerciseInfo(false)}
      />
    </div>
  );
}

export default Session;
